import * as apiClient from "@/apiclient/";
import iBenefit from "@/types/iBenefit";
import helper from "./helper";
import iCOBRABenefit from "@/types/iCOBRABenefit";
import iDependent from "@/types/iDependent";

const benefitMapper = {
  mapApiBenefitsToBenefits(
    apiBenefits: apiClient.OpenEnrollmentBenefitDto[],
    dependents: iDependent[]
  ): iBenefit[] {
    return apiBenefits.map((benefit) => {
      // set up our subsidy details for the benefit
      let subsidyTotal = 0.0;
      let subsidyDetails = "";
      let previousEndDate = "";
      let hasOpenEndDate = false;

      if (
        benefit.subsidies != undefined &&
        benefit.subsidies != null &&
        benefit.subsidies.length > 0
      ) {
        benefit.subsidies!.forEach((subsidy) => {
          subsidyTotal = subsidyTotal + subsidy.amount! * -1;
          if (!hasOpenEndDate) {
            if (subsidy.endDate == null || subsidy.endDate == "") {
              hasOpenEndDate = true;
            }
            if (previousEndDate != "" && previousEndDate != subsidy.endDate) {
              hasOpenEndDate = true;
            }
            previousEndDate = subsidy.endDate!;
          }
        });
      }

      if (hasOpenEndDate) {
        subsidyDetails = "* multiple end dates may apply";
      } else if (previousEndDate) {
        subsidyDetails =
          "* subsidy ends on " + helper.formatDate(previousEndDate);
      }

      // Calculate subsidyTotal and subsidyDetails for each tier
      const tiersWithSubsidy = (benefit.tiers || []).map((tier) => {
        // Ensure subsidies is never null
        const safeTier = { ...tier, subsidies: tier.subsidies ?? undefined };
        const { subsidyTotal, subsidyDetails } =
          helper.calculateSubsidy(safeTier);
        return {
          ...tier,
          subsidyTotal,
          subsidyDetails,
        };
      });

      let isWaived: boolean | null = null;
      if (typeof benefit.isSelected !== "undefined") {
        if (benefit.isSelected) {
          isWaived = false;
        } else {
          isWaived = true;
        }
      } else {
        isWaived = true;
      }

      return {
        isOpen: false,
        benefitId: benefit.benefitId!,
        benefitCategoryId: benefit.benefitCategoryId,
        benefitRateTypeId: benefit.benefitRateTypeId!,
        benefitRateTypeName: benefit.benefitRateTypeName!,
        carrierId: benefit.carrierId!,
        carrierName: benefit.carrierName!,
        tiers: tiersWithSubsidy,
        planDescription: benefit.planDescription!,
        planName: benefit.planName!,
        planType: benefit.planType!,
        singleEmployeeRate: benefit.singleEmployeeRate,
        singleMonthlyPremiumRate: benefit.singleMonthlyPremiumRate,
        uniqueEmployeeRate: benefit.uniqueEmployeeRate,
        uniqueMonthlyPremiumRate: benefit.uniqueMonthlyPremiumRate,
        dependents: dependents.map((d) => ({ ...d })), // <-- assign dependents to each benefit
        dependentValMsg: "",
        benefitType: 0,
        subsidies: benefit.subsidies!,
        subsidyTotal: subsidyTotal,
        subsidyDetails: subsidyDetails,
        documents: benefit.documents!,
        documentsOpen: false,
        isWaived: isWaived,
        electionDependentBenefits: benefit.electionDependentIds || [],
        hasUploadedRequiredFile: false,
        uniqueRates: benefit.uniqueRates,
        singleRates: benefit.singleRates,
        currentlyEnrolled: benefit.currentlyEnrolled || false,
        benefitIds: benefit.benefitIds,
      };
    });
  },
  mapApiBundleToBenefits(
    apiBundles: apiClient.OpenEnrollmentBenefitsBundleDto[],
    dependents: iDependent[]
  ): iBenefit[] {
    return apiBundles.map((bundle) => {
      // Map each benefit in the bundle using mapApiBenefitsToBenefits
      const mappedBenefits = this.mapApiBenefitsToBenefits(
        bundle.benefits || [],
        dependents
      ).map((benefit) => ({
        ...benefit,
        dependents: dependents.map((d) => ({ ...d })), // <-- assign dependents to each child benefit
        documentsOpen: false,
      }));

      const requiredFileUploadedBenefits = bundle.benefits?.filter(
        (benefit) => {
          return false;
        }
      );

      // Gather all unique electionDependentIds from mappedBenefits
      const allElectionDependentIds = mappedBenefits
        .flatMap((b) => b.electionDependentBenefits || [])
        .filter((id, idx, arr) => id != null && arr.indexOf(id) === idx);

      const initialTotal = 0;
      let subsidyTotal = 0;
      let subsidyMessage = "";
      if (bundle.subsidies && bundle.subsidies.length > 0) {
        subsidyTotal = bundle.subsidies?.reduce(
          (accumulator, subsidy) => accumulator + -1 * (subsidy.amount ?? 0),
          initialTotal
        );
        const areDatesDifferent =
          bundle.subsidies?.filter((subsidy) => {
            return bundle!.subsidies![0].endDate == subsidy.endDate;
          }).length != bundle.subsidies?.length;
        if (areDatesDifferent) {
          subsidyMessage = "* multiple end dates may apply";
        } else if (!areDatesDifferent) {
          subsidyMessage =
            "* subsidy ends on " +
            helper.formatDate(bundle.subsidies[0].endDate!);
        }
      }

      const benefitIds = mappedBenefits.flatMap((b) => {
        return b.benefitIds ? b.benefitIds : [];
      });

      return {
        isOpen: false,
        benefitBundleId: bundle.benefitBundleId!,
        tiers: bundle.tiers!,
        bundleName: bundle.bundleName!,
        dependents: dependents.map((d) => ({ ...d })), // <-- assign dependents to the bundle itself
        dependentValMsg: "",
        benefits: mappedBenefits as iBenefit[], // <-- ensure dependents property exists and type is iBenefit
        benefitId: 99000 + bundle.benefitBundleId!,
        benefitType: 1,
        benefitRateTypeId: bundle.benefitRateTypeId!,
        singleEmployeeRate: bundle.singleEmployeeRate,
        singleMonthlyPremiumRate: bundle.singleMonthlyPremiumRate,
        uniqueEmployeeRate: bundle.uniqueEmployeeRate,
        uniqueMonthlyPremiumRate: bundle.uniqueMonthlyPremiumRate,
        hasUploadedRequiredFile: requiredFileUploadedBenefits!.length > 0,
        isWaived: !bundle.isSelected,
        electionDependentBenefits: allElectionDependentIds,
        singleRates: bundle.singleRates,
        subsidyTotal: subsidyTotal,
        subsidyDetails: subsidyMessage,
        currentlyEnrolled: !mappedBenefits.some(
          (child) => child.currentlyEnrolled === false
        ),
        benefitIds: benefitIds,
      };
    });
  },
  mapApiDependentsToDependents(
    apiDependents: apiClient.GetAllDependentDto[]
  ): iDependent[] {
    return apiDependents.map((dependent) => {
      return {
        electionDependentId: dependent.electionDependentId!,
        firstName: dependent.firstName!,
        lastName: dependent.lastName!,
        relationshipName: dependent.relationshipName!,
        dependentCount: 0,
        isSelected: false,
        disabled: false,
        customerParticipantId: 0,
      };
    });
  },
  mapCOBRAApiBenefitsToBenefits(
    apiBenefits: apiClient.ParticipantBenefitsDto[]
  ): iCOBRABenefit[] {
    return apiBenefits.map((benefit) => {
      // Calculate subsidyTotal and subsidyDetails for each coverageOption
      const coverageOptionsWithSubsidy = (benefit.coverageOptions || []).map(
        (option) => {
          let optionSubsidyTotal = 0.0;
          let optionSubsidyDetails = "";
          let optionPreviousEndDate = "";
          let optionHasOpenEndDate = false;

          if (option.subsidies && option.subsidies.length > 0) {
            option.subsidies.forEach((subsidy) => {
              optionSubsidyTotal += subsidy.amount! * -1;
              if (!optionHasOpenEndDate) {
                if (subsidy.endDate == null || subsidy.endDate == "") {
                  optionHasOpenEndDate = true;
                }
                if (
                  optionPreviousEndDate !== "" &&
                  optionPreviousEndDate !== subsidy.endDate
                ) {
                  optionHasOpenEndDate = true;
                }
                optionPreviousEndDate = subsidy.endDate!;
              }
            });
          }

          if (optionHasOpenEndDate) {
            optionSubsidyDetails = "* multiple end dates may apply";
          } else if (optionPreviousEndDate) {
            optionSubsidyDetails =
              "* subsidy ends on " + helper.formatDate(optionPreviousEndDate);
          }

          return {
            ...option,
            subsidyTotal: optionSubsidyTotal,
            subsidyDetails: optionSubsidyDetails,
          };
        }
      );

      // set up our subsidy details for the benefit
      let subsidyTotal = 0.0;
      let subsidyDetails = "";
      let previousEndDate = "";
      let hasOpenEndDate = false;

      if (
        benefit.subsidies != undefined &&
        benefit.subsidies != null &&
        benefit.subsidies.length > 0
      ) {
        benefit.subsidies!.forEach((subsidy) => {
          //console.log("subsidy " + JSON.stringify(subsidy));
          //console.log("subsidy.amount " + subsidy.amount);

          subsidyTotal = subsidyTotal + subsidy.amount! * -1;
          if (!hasOpenEndDate) {
            if (subsidy.endDate == null || subsidy.endDate == "") {
              hasOpenEndDate = true;
            }
            if (previousEndDate != "" && previousEndDate != subsidy.endDate) {
              hasOpenEndDate = true;
            }
            previousEndDate = subsidy.endDate!;
          }
        });
      }

      if (hasOpenEndDate) {
        subsidyDetails = "* multiple end dates may apply";
      } else if (previousEndDate) {
        subsidyDetails =
          "* subsidy ends on " + helper.formatDate(previousEndDate);
      }

      return {
        isOpen: false,
        benefitId: benefit.benefitId!,
        benefitIds: benefit.benefitIds,
        benefitRateTypeId: benefit.benefitRateTypeId!,
        benefitRateTypeName: benefit.benefitRateTypeName!,
        carrierId: benefit.carrierId!,
        carrierName: benefit.carrierName!,
        coverageOptions: coverageOptionsWithSubsidy,
        customerParticipantID: benefit.customerParticipantID!,
        eBenefitId: benefit.eBenefitId!,
        effectiveDate: benefit.effectiveDate!,
        initialAmount: benefit.initialAmount!,
        isWaived: benefit.isWaived!,
        planDescription: benefit.planDescription!,
        planName: benefit.planName!,
        planType: benefit.planType!,
        electionDependentBenefits: benefit.electionDependentBenefits!,
        singleEmployeeRate: benefit.singleEmployeeRate,
        singleMonthlyPremiumRate: benefit.singleMonthlyPremiumRate,
        uniqueEmployeeRate: benefit.uniqueEmployeeRate,
        uniqueMonthlyPremiumRate: benefit.uniqueMonthlyPremiumRate,
        dependents: [],
        benefitEndDate: benefit.benefitEndDate!,
        dependentValMsg: "",
        customerParticipantId: benefit.customerParticipantID!,
        subsidies: benefit.subsidies!,
        subsidyTotal: subsidyTotal,
        subsidyDetails: subsidyDetails,
        singleRates: benefit.singleRates,
        uniqueRates: benefit.uniqueRates,
      };
    });
  },
  mapCOBRAApiDependentsToDependents(
    apiDependents: apiClient.DependentResponseDto[]
  ): iDependent[] {
    return apiDependents.map((dependent) => {
      return {
        electionDependentId: dependent.electionDependentId!,
        firstName: dependent.firstName!,
        lastName: dependent.lastName!,
        relationshipName: "",
        dependentCount: 0,
        isSelected: false,
        disabled: false,
        customerParticipantId: dependent.customerParticipantId!,
      };
    });
  },
};

export default benefitMapper;
